import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  #ignoreNextAccountToggleClick = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);

    this.#accountMenus.forEach((menu) => {
      menu.addEventListener('click', this.#onAccountMenuClick);
      const toggle = this.#getAccountMenuToggle(menu);
      toggle?.addEventListener('pointerdown', this.#onAccountTogglePointerDown);
      toggle?.addEventListener('click', this.#onAccountToggleClick);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);

    this.#accountMenus.forEach((menu) => {
      menu.removeEventListener('click', this.#onAccountMenuClick);
      const toggle = this.#getAccountMenuToggle(menu);
      toggle?.removeEventListener('pointerdown', this.#onAccountTogglePointerDown);
      toggle?.removeEventListener('click', this.#onAccountToggleClick);
    });
  }

  /**
   * @returns {HTMLElement[]}
   */
  get #accountMenus() {
    return Array.from(this.querySelectorAll('[data-header-account-menu]')).filter((menu) => menu instanceof HTMLElement);
  }

  /**
   * @param {HTMLElement} menu
   * @returns {HTMLButtonElement | null}
   */
  #getAccountMenuToggle(menu) {
    const toggle = menu.querySelector('[data-header-account-toggle]');
    return toggle instanceof HTMLButtonElement ? toggle : null;
  }

  /**
   * @param {HTMLElement} menu
   * @returns {HTMLElement | null}
   */
  #getAccountMenuPanel(menu) {
    const panel = menu.querySelector('.inknfiber-account-menu__panel');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} menu
   * @returns {boolean}
   */
  #isAccountMenuOpen(menu) {
    return menu.classList.contains('is-open');
  }

  /**
   * @param {HTMLElement} menu
   * @param {boolean} open
   */
  #setAccountMenuOpen(menu, open) {
    const toggle = this.#getAccountMenuToggle(menu);
    const panel = this.#getAccountMenuPanel(menu);

    if (panel) {
      if (open) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    }

    menu.classList.toggle('is-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
  }

  /**
   * @param {HTMLElement} currentMenu
   */
  #closeOtherAccountMenus(currentMenu) {
    this.#accountMenus.forEach((menu) => {
      if (menu !== currentMenu) this.#setAccountMenuOpen(menu, false);
    });
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.data?.itemCount ?? event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  /**
   * Closes account menus when the customer clicks outside them.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;

    this.#accountMenus.forEach((menu) => {
      if (this.#isAccountMenuOpen(menu) && !menu.contains(event.target)) {
        this.#setAccountMenuOpen(menu, false);
      }
    });
  };

  /**
   * Toggles account menus before mobile browsers fire the synthetic click/default details toggle.
   * @param {PointerEvent} event
   */
  #onAccountTogglePointerDown = (event) => {
    const toggle = event.currentTarget;
    if (!(toggle instanceof HTMLElement)) return;

    const menu = toggle.closest('[data-header-account-menu]');
    if (!(menu instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const shouldOpen = !this.#isAccountMenuOpen(menu);
    this.#closeOtherAccountMenus(menu);
    this.#setAccountMenuOpen(menu, shouldOpen);
    this.#ignoreNextAccountToggleClick = true;
  };

  /**
   * Handles keyboard activation and swallows the synthetic click after pointerdown on touch browsers.
   * @param {MouseEvent} event
   */
  #onAccountToggleClick = (event) => {
    const toggle = event.currentTarget;
    if (!(toggle instanceof HTMLElement)) return;

    const menu = toggle.closest('[data-header-account-menu]');
    if (!(menu instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.#ignoreNextAccountToggleClick) {
      this.#ignoreNextAccountToggleClick = false;
      return;
    }

    const shouldOpen = !this.#isAccountMenuOpen(menu);
    this.#closeOtherAccountMenus(menu);
    this.#setAccountMenuOpen(menu, shouldOpen);
  };

  /**
   * Closes the menu when a menu link is chosen.
   * @param {MouseEvent} event
   */
  #onAccountMenuClick = (event) => {
    const menu = event.currentTarget;
    if (!(menu instanceof HTMLElement)) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[role="menuitem"]')) {
      this.#setAccountMenuOpen(menu, false);
    }
  };

  /**
   * Closes account menus when Escape is pressed.
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;

    this.#accountMenus.forEach((menu) => {
      if (!this.#isAccountMenuOpen(menu)) return;

      this.#setAccountMenuOpen(menu, false);
      this.#getAccountMenuToggle(menu)?.focus();
    });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
